import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Plus, Trash2, Link2, UserPlus, X, Check, Upload, Loader2, Search, Eye, Image as ImageIcon, Maximize2, Monitor, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { B2bProducts } from "@/components/b2b/B2bProducts";

export const B2bStores = () => {
  const confirm = useConfirm();
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/b2b/admin/stores").then((r) => setStores(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/users").then((r) => setEmployees((r.data || []).filter((u) => ["employee", "admin"].includes(u.role)))).catch(() => {});
  }, []);

  const createStore = async () => {
    if (!newName.trim()) return toast.error("Podaj nazwę sklepu");
    setBusy(true);
    try {
      await api.post("/b2b/admin/stores", { name: newName.trim() });
      setNewName(""); toast.success("Sklep utworzony"); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="b2b-stores">
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-display text-sm font-bold uppercase tracking-wider flex items-center gap-2">
          <Store className="h-4 w-4 text-amber-400" />Sklepy prywatne B2B
        </h3>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Wydzielone sklepy dla wybranych klientów (np. REDFOX). Klienci logują się tym samym ekranem co dotychczas.
          Produkty i zamówienia sklepu są prywatne — nie trafiają do sklepu publicznego EMPRO.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input data-testid="b2b-new-store-name" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Nazwa nowego sklepu, np. REDFOX" className="sm:max-w-xs" />
          <Button data-testid="b2b-create-store" onClick={createStore} disabled={busy}
            className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><Plus className="h-4 w-4" />Utwórz sklep</Button>
        </div>
      </div>

      {stores.length === 0 && (
        <p data-testid="b2b-empty" className="text-sm text-muted-foreground">Brak sklepów prywatnych. Utwórz pierwszy powyżej.</p>
      )}

      {stores.map((s) => (
        <StoreCard key={s.id} store={s} customers={customers} employees={employees} confirm={confirm} onChanged={load} />
      ))}
    </div>
  );
};

const StoreCard = ({ store, customers, employees, confirm, onChanged }) => {
  const navigate = useNavigate();
  const [s, setS] = useState(store);
  const [name, setName] = useState(store.name);
  const [custSearch, setCustSearch] = useState("");
  const [selected, setSelected] = useState(new Set((store.linked_customers || []).map((c) => c.id)));
  const [accounts, setAccounts] = useState([]);
  const [email, setEmail] = useState("");
  const [uploading, setUploading] = useState({ logo: false, banner: false });

  useEffect(() => { setS(store); setName(store.name); setSelected(new Set((store.linked_customers || []).map((c) => c.id))); }, [store]);
  useEffect(() => { loadAccounts(); /* eslint-disable-next-line */ }, [store.id]);

  const loadAccounts = () => api.get(`/b2b/admin/stores/${store.id}/accounts`).then((r) => setAccounts(r.data)).catch(() => {});

  const patch = async (payload, okMsg) => {
    try { const { data } = await api.put(`/b2b/admin/stores/${store.id}`, payload); setS(data); if (okMsg) toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveName = () => { if (name.trim() && name.trim() !== s.name) patch({ name: name.trim() }, "Nazwa zapisana"); };

  const toggleActive = (v) => patch({ active: v }, v ? "Sklep aktywny" : "Sklep ukryty");

  const uploadImg = async (e, kind) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await patch(kind === "logo" ? { logo_file_id: data.id } : { banner_file_id: data.id }, "Grafika zapisana");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const saveLinks = async () => {
    try {
      const { data } = await api.put(`/b2b/admin/stores/${store.id}/linked-customers`, { customer_ids: [...selected] });
      setS(data); toast.success("Powiązane kartoteki zapisane"); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setDefault = async (cid) => {
    try { const { data } = await api.put(`/b2b/admin/stores/${store.id}/default-customer`, { customer_id: cid }); setS(data); toast.success("Kartoteka domyślna ustawiona"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const assign = async () => {
    if (!email.trim()) return toast.error("Podaj e-mail konta");
    try { await api.post(`/b2b/admin/stores/${store.id}/accounts`, { email: email.trim() }); setEmail(""); toast.success("Konto przypisane"); loadAccounts(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const revoke = async (acc) => {
    if (!(await confirm({ title: "Odebrać dostęp?", description: `Konto ${acc.email} straci dostęp do sklepu ${s.name}.`, confirmText: "Odbierz dostęp", danger: true }))) return;
    try { await api.delete(`/b2b/admin/stores/${store.id}/accounts/${acc.id}`); toast.success("Dostęp odebrany"); loadAccounts(); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeStore = async () => {
    if (!(await confirm({ title: `Usunąć sklep ${s.name}?`, description: "Konta zachowają swoje dane, ale stracą dostęp do tego sklepu. Operacja nie usuwa kartotek ani zamówień.", confirmText: "Usuń sklep", danger: true }))) return;
    try { await api.delete(`/b2b/admin/stores/${store.id}`); toast.success("Sklep usunięty"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleCust = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = customers.filter((c) => {
    const q = custSearch.trim().toLowerCase();
    if (!q) return selected.has(c.id);
    return (c.name || "").toLowerCase().includes(q) || (c.short_name || "").toLowerCase().includes(q) || (c.nip || "").includes(q);
  });

  return (
    <div data-testid={`b2b-store-${store.id}`} className="rounded-xl border border-border bg-card p-5 space-y-5">
      {/* Nagłówek */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {s.logo_file_id
            ? <img src={`${API}/files/${s.logo_file_id}`} alt="" className="h-10 w-10 rounded-lg object-cover bg-[#0D1117]" />
            : <div className="h-10 w-10 rounded-lg bg-secondary grid place-items-center"><Store className="h-5 w-5 text-muted-foreground" /></div>}
          <Input data-testid={`b2b-store-name-${store.id}`} value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName}
            className="h-9 w-48 font-display text-lg font-extrabold" />
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${s.can_order ? "bg-emerald-400/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>
            {s.can_order ? "Gotowy do zamówień" : "Brak kartoteki domyślnej"}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none">
            <Switch data-testid={`b2b-store-active-${store.id}`} checked={s.active} onCheckedChange={toggleActive} />
            {s.active ? "Widoczny" : "Ukryty"}
          </label>
          <Button data-testid={`b2b-store-preview-${store.id}`} size="sm" variant="outline" className="h-8 gap-1.5"
            onClick={() => navigate(`/podglad-sklepu/${store.id}`)}><Eye className="h-4 w-4" />Podgląd</Button>
          <Button data-testid={`b2b-store-delete-${store.id}`} size="icon" variant="ghost" className="h-8 w-8 text-rose-400" onClick={removeStore}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Logo + baner */}
      <div className="flex flex-wrap gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Logo</Label>
          <label className="mt-1 flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-amber-400">
            {uploading.logo ? <Loader2 className="h-4 w-4 animate-spin" /> : s.logo_file_id ? <img src={`${API}/files/${s.logo_file_id}`} alt="" className="h-full w-full rounded-lg object-cover" /> : <Upload className="h-4 w-4" />}
            <input data-testid={`b2b-store-logo-${store.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e, "logo")} />
          </label>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Baner</Label>
          <label className="mt-1 flex h-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-amber-400 overflow-hidden">
            {uploading.banner ? <Loader2 className="h-4 w-4 animate-spin" /> : s.banner_file_id ? <img src={`${API}/files/${s.banner_file_id}`} alt="" className="h-full w-full object-cover" /> : <span className="flex items-center gap-2 text-xs"><Upload className="h-4 w-4" />Dodaj baner sklepu</span>}
            <input data-testid={`b2b-store-banner-${store.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e, "banner")} />
          </label>
        </div>
      </div>

      {/* Podgląd grafik + zalecane wymiary */}
      <BrandingPanel store={s} />

      {/* Powiązane kartoteki */}
      <div className="rounded-lg border border-border bg-background/50 p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Link2 className="h-4 w-4 text-amber-400" />Powiązane kartoteki klienta</p>
          <Button data-testid={`b2b-save-links-${store.id}`} size="sm" variant="outline" className="h-8 gap-1.5" onClick={saveLinks}><Check className="h-3.5 w-3.5" />Zapisz powiązania</Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Powiąż istniejące kartoteki (np. REDFOX i RedFox RACING) z tym sklepem. Nie scala kartotek ani ich danych.</p>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid={`b2b-cust-search-${store.id}`} value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Szukaj kartoteki po nazwie lub NIP…" className="h-9 pl-8" />
        </div>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {filtered.length === 0 && <p className="text-[11px] text-muted-foreground px-1">{custSearch ? "Brak wyników." : "Wyszukaj kartotekę, aby ją powiązać."}</p>}
          {filtered.map((c) => (
            <label key={c.id} data-testid={`b2b-cust-opt-${store.id}-${c.id}`} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary cursor-pointer">
              <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleCust(c.id)} />
              <span className="text-sm font-medium truncate">{c.name}</span>
              {c.nip && <span className="text-[11px] text-muted-foreground">NIP {c.nip}</span>}
            </label>
          ))}
        </div>
      </div>

      {/* Kartoteka domyślna */}
      <div className="rounded-lg border border-border bg-background/50 p-4 space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider">Kartoteka domyślna (cel nowych zamówień)</p>
        <p className="text-[11px] text-muted-foreground">Do czasu wskazania kartoteki sklep nie przyjmuje rzeczywistych zamówień.</p>
        <Select value={s.default_customer_id || ""} onValueChange={setDefault}>
          <SelectTrigger data-testid={`b2b-default-cust-${store.id}`} className="h-9 sm:w-80"><SelectValue placeholder="Wybierz kartotekę domyślną" /></SelectTrigger>
          <SelectContent>
            {(s.linked_customers || []).map((c) => <SelectItem key={c.id} value={c.id} data-testid={`b2b-default-opt-${store.id}-${c.id}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Handlowiec sklepu (odbiorca prowizji) */}
      <div className="rounded-lg border border-border bg-background/50 p-4 space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider">Handlowiec sklepu (odbiorca prowizji)</p>
        <p className="text-[11px] text-muted-foreground">Prowizja z zamówień REDFOX trafia do tego handlowca. Jeśli nie wskazano — właściciel kartoteki domyślnej.</p>
        <Select value={s.sales_rep_id || "__none__"} onValueChange={(v) => patch({ sales_rep_id: v === "__none__" ? "" : v }, "Handlowiec sklepu zapisany")}>
          <SelectTrigger data-testid={`b2b-sales-rep-${store.id}`} className="h-9 sm:w-80"><SelectValue placeholder="Właściciel kartoteki (domyślnie)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Właściciel kartoteki (domyślnie)</SelectItem>
            {(employees || []).map((u) => <SelectItem key={u.id} value={u.id} data-testid={`b2b-rep-opt-${store.id}-${u.id}`}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Konta z dostępem */}
      <div className="rounded-lg border border-border bg-background/50 p-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"><UserPlus className="h-4 w-4 text-amber-400" />Konta z dostępem ({accounts.length})</p>
        <p className="text-[11px] text-muted-foreground">Przypisz istniejące konto klienta (po e-mailu). Konto musi już istnieć — nie wysyłamy automatycznych zaproszeń.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input data-testid={`b2b-assign-email-${store.id}`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e-mail istniejącego konta" className="sm:max-w-xs h-9" />
          <Button data-testid={`b2b-assign-btn-${store.id}`} size="sm" variant="outline" className="h-9 gap-1.5" onClick={assign}><UserPlus className="h-4 w-4" />Przypisz dostęp</Button>
        </div>
        <div className="space-y-1">
          {accounts.length === 0 && <p className="text-[11px] text-muted-foreground px-1">Brak przypisanych kont.</p>}
          {accounts.map((a) => (
            <div key={a.id} data-testid={`b2b-account-${store.id}-${a.id}`} className="flex items-center justify-between gap-2 rounded-md bg-secondary px-3 py-1.5">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{a.email}</p>
                {(a.name || a.provider) && <p className="text-[11px] text-muted-foreground truncate">{a.name}{a.name && a.provider ? " · " : ""}{a.provider === "google" ? "Google" : "e-mail"}{a.verified ? "" : " · niezweryfikowane"}</p>}
              </div>
              <Button data-testid={`b2b-revoke-${store.id}-${a.id}`} size="icon" variant="ghost" className="h-7 w-7 text-rose-400 shrink-0" onClick={() => revoke(a)}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Produkty sklepu */}
      <B2bProducts storeId={store.id} storeName={s.name} />
    </div>
  );
};

// Podgląd grafik marki (logo + baner) oraz zalecane wymiary.
// Wartości i kadrowanie WYPROWADZONE z faktycznej implementacji storefrontu:
//  - Logo: nagłówek `h-9` (36 px) × `max-w-[160px]`, object-contain → nigdy nie przycinane.
//  - Baner: komputer `aspect-[16/5]` = 3,2:1, telefon `aspect-[16/6]` = 2,667:1, object-cover (przycinany, wyśrodkowany).
const EmptyState = ({ text, tid }) => (
  <div data-testid={tid} className="flex h-20 items-center justify-center rounded-md border-2 border-dashed border-border text-[11px] text-muted-foreground">{text}</div>
);

const BrandingPanel = ({ store }) => {
  const [zoom, setZoom] = useState(null);
  const logo = store.logo_file_id ? `${API}/files/${store.logo_file_id}` : "";
  const banner = store.banner_file_id ? `${API}/files/${store.banner_file_id}` : "";

  return (
    <div data-testid={`b2b-branding-${store.id}`} className="rounded-lg border border-border bg-background/50 p-4 space-y-5">
      <p className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"><ImageIcon className="h-4 w-4 text-amber-400" />Podgląd grafik i zalecane wymiary</p>

      {/* LOGO */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Logo — pełny podgląd (bez przycięcia)</span>
          {logo && <button data-testid={`b2b-logo-zoom-${store.id}`} onClick={() => setZoom(logo)} className="flex items-center gap-1 text-[11px] font-medium text-amber-400 hover:text-amber-300"><Maximize2 className="h-3 w-3" />Powiększ</button>}
        </div>
        {logo
          ? <div className="inline-flex rounded-md border border-border bg-[#0a0a0b] p-3"><img data-testid={`b2b-logo-full-${store.id}`} src={logo} alt="Logo sklepu" className="max-h-24 w-auto object-contain" /></div>
          : <EmptyState text="Brak logo — w nagłówku pokaże się nazwa sklepu" tid={`b2b-logo-empty-${store.id}`} />}
        <div>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Tak wygląda w nagłówku sklepu:</span>
          <div className="mt-1 flex h-16 items-center rounded-md border border-red-600/30 bg-[#0a0a0b] px-4">
            {logo ? <img src={logo} alt="" className="h-9 w-auto max-w-[160px] object-contain" />
              : <span className="uppercase tracking-[0.18em] text-lg italic text-neutral-100" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{store.name || "Mój sklep"}</span>}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Zalecane: <b>poziome logo PNG z przezroczystym tłem</b> (nagłówek jest ciemny). Proporcje do <b>~4,4:1</b> (160:36). Zalecany plik <b>~480×108 px</b>. W nagłówku skalowane do wys. 36 px, maks. szer. 160 px, <b>object-contain — bez przycinania</b> (dowolne proporcje się zmieszczą, tylko wyższe logo będzie węższe).
        </p>
      </div>

      {/* BANER */}
      <div className="space-y-2 border-t border-border pt-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Baner — pełny podgląd (bez przycięcia)</span>
          {banner && <button data-testid={`b2b-banner-zoom-${store.id}`} onClick={() => setZoom(banner)} className="flex items-center gap-1 text-[11px] font-medium text-amber-400 hover:text-amber-300"><Maximize2 className="h-3 w-3" />Powiększ</button>}
        </div>
        {banner ? (
          <div className="grid gap-4 sm:grid-cols-2 pt-1">
            <div>
              <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Monitor className="h-3.5 w-3.5" />Komputer — cały baner</span>
              <div data-testid={`b2b-banner-desktop-${store.id}`} className="overflow-hidden rounded-md border border-border bg-neutral-900">
                <img data-testid={`b2b-banner-full-${store.id}`} src={banner} alt="Baner sklepu" className="block w-full h-auto" />
              </div>
            </div>
            <div>
              <span className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><Smartphone className="h-3.5 w-3.5" />Telefon — cały baner</span>
              <div data-testid={`b2b-banner-mobile-${store.id}`} className="mx-auto overflow-hidden rounded-md border border-border bg-neutral-900" style={{ maxWidth: 300 }}>
                <img src={banner} alt="" className="block w-full h-auto" />
              </div>
            </div>
          </div>
        ) : (
          <EmptyState text="Brak banera — w sklepie pokaże się domyślny baner z nazwą sklepu" tid={`b2b-banner-empty-${store.id}`} />
        )}
        <div className="rounded-md border border-amber-400/30 bg-amber-400/10 p-2.5 text-[11px] leading-relaxed text-amber-100/90">
          <b>Baner wyświetlany jest w całości — bez przycinania — na komputerze i telefonie.</b> Skaluje się na pełną szerokość, a wysokość dobiera proporcjonalnie, więc oba logotypy zawsze pozostają widoczne. Zalecana proporcja pozioma <b>~3:1</b> (np. <b>2000×667 px</b>), aby baner nie był zbyt wysoki na telefonie.
        </div>
      </div>

      {zoom && (
        <div data-testid={`b2b-branding-zoom-overlay-${store.id}`} onClick={() => setZoom(null)} className="fixed inset-0 z-[100] grid place-items-center bg-black/80 p-6 cursor-zoom-out">
          <img src={zoom} alt="Podgląd" className="max-h-[90vh] max-w-[90vw] object-contain" />
          <button className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
};
